/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Maybe } from 'graphql/jsutils/Maybe';

import { Button, Link, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { EntWebsiteProofPointFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import { FootnoteContext, createAnchor, createReverseAnchor } from 'bundles/ent-website/components/FootnoteContext';
import { MAX_PAGE_CONTAINER_WIDTH } from 'bundles/ent-website/constants/layout';
import type { LinkIndividualLink } from 'bundles/ent-website/contentful/core/Navigation';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type ContentfulColor = {
  id: string;
  name: string;
  value: string;
};

type Props = {
  proofPoints: Array<EntWebsiteProofPointFragment>;
  heading?: Maybe<string>;
  subheading?: Maybe<string>;
  backgroundColor?: Maybe<ContentfulColor>;
  fontColor?: Maybe<ContentfulColor>;
  cta?: Maybe<LinkIndividualLink>;
  ctaButtonVariant?: Maybe<string>;
};

/**
 * How to layout the proof point item:
 *
 * The ProofPoints should be placed right after a `<BlockLayout>`. The page layout
 * should look like this:
 *
 * <BlockLayout>
 * .-------------------------------------------------------------.
 * |    |    :              :    |    :                :    |    |
 * `-------------------------------------------------------------'
 *         ^-------- padding from BlockLayout.styles.block (md/sm/xs=64/32/24)
 *    ^--------------- padding from BlockLayout.PgeGridContainer (48/16/16)
 *
 * <ProofPoints>
 * The basic idea is:
 *
 * 1. Use `.layout { float: left }` to implement word wrapping so that `.layout` could push the content
 *    in the next block away
 * 2. `.layout` needs the same width setting as `BlockLayout` so manually sets its `max-width` with theme
 * 3. Use `.layout { left: 50% }` to move it to the center of the content area
 * 4. To avoid `.layout` from enlarging the page, set `width: 50%` so that it occupies only the right half of the page
 * 5. In the inner `wrapper`, use `margin-left: -100%` to move the `.wrapper` back to the left edge of the content area.
 *    Usually this should be `margin-left: -50%` but since we shrinked the `.layout` width to 50%,
 *    we need `50% * 2` here to scale it back to the original size
 * 6. Same for `max-width`, set `max-width: 200%` which normally would have been 100%
 * 7. Set `.wrapper { padding-left / right }` to be equal to the margin-left/right of the `PageGridContainer
 * 8. Further, set `.proofPoint { margin-left/right } to be equal to the padding-left/right` of the `BlockLayout`
 */
const styles = {
  layout: () => css`
    position: relative;
    padding: 0 !important;
    float: left;
    left: 50%;
    width: 50%;
    max-width: ${breakpoints.values[MAX_PAGE_CONTAINER_WIDTH] / 2}px;
  `,

  wrapper: css`
    margin-left: -100%;
    max-width: 200%;
  `,

  headingContainer: () => css`
    padding: var(--cds-spacing-600) var(--cds-spacing-800) 0;
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400) var(--cds-spacing-200) 0;
    }
  `,

  // According to the layout guide above, the positions are calculated as:
  //   margin-left = -50% + padding from BlockLayout + padidng from PageGridContainer
  //   max-width   = 100% - 2 * (padding from BlockLayout + padding from PageGridContainer)
  proofPoints: (_: Theme, backgroundColor?: string, fontColor?: string) => css`
    background: ${backgroundColor ?? 'var(--cds-color-grey-975)'};
    border-radius: 8px;
    color: ${fontColor ?? 'var(--cds-color-white-0)'};
    margin-top: calc(var(--cds-spacing-800) * -1);
    margin-bottom: var(--cds-spacing-1400);
    display: flex;
    flex-direction: column;
    margin-left: var(--cds-spacing-800);
    margin-right: var(--cds-spacing-800);

    ${breakpoints.down('sm')} {
      margin-left: var(--cds-spacing-200);
      margin-right: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-800);
    }

    ${breakpoints.down('xs')} {
      margin-left: var(--cds-spacing-200);
      margin-right: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-800);
    }

    * {
      word-break: normal;
    }
  `,

  proofPointsCollection: () => css`
    display: flex;
    flex-direction: row;

    ${breakpoints.down('xs')} {
      border-radius: 6px;
      flex-direction: column;
    }
  `,

  proofPoint: () => css`
    position: relative;
    margin: var(--cds-spacing-400) 0;
    padding: var(--cds-spacing-200) var(--cds-spacing-800);
    flex: 1;
    display: flex;
    flex-direction: row;
    border-right: 1px solid var(--cds-color-grey-600);
    align-items: center;

    &:last-child {
      border-right: 0;
    }

    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-400) 0;
      padding: 0 var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      border-right: 0;
      border-bottom: 1px solid var(--cds-color-grey-500);
      margin: 0 var(--cds-spacing-200);
      padding: var(--cds-spacing-400) 0;

      &:last-child {
        border-bottom: 0;
      }
    }
  `,

  sourceLink: () => css`
    font-size: 14px;
    color: var(--cds-color-neutral-primary-weak) !important;
    text-decoration: underline;

    &:hover {
      text-decoration: underline;
    }
  `,

  sourceLinkContainer: () => css`
    position: absolute;
    right: 16px;
    bottom: -16px;

    ${breakpoints.down('xs')} {
      bottom: 8px;
      right: 0;
    }
  `,

  keyMetricAndUnit: css`
    display: flex;
    flex-direction: column;
  `,

  keyMetric: () => css`
    font-size: 40px !important;
    font-weight: 600 !important;
    line-height: 40px !important;
    margin-right: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      font-size: 32px !important;
      line-height: 32px !important;
      margin-right: var(--cds-spacing-100);
    }
  `,

  unit: () => css`
    font-size: 1.5rem !important;
    margin-top: var(--cds-spacing-100);
  `,

  description: () => css`
    font-size: 16px !important;
    line-height: 19px !important;

    ${breakpoints.down('sm')} {
      font-size: 14px !important;
    }
  `,

  cta: () => css`
    width: fit-content;
    margin-left: var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-600);

    ${breakpoints.down('sm')} {
      margin-left: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-400);
    }
  `,
};

function ProofPoints({ proofPoints, heading, subheading, backgroundColor, fontColor, cta, ctaButtonVariant }: Props) {
  const theme = useTheme();
  const { registerFootnote, getFootnote } = React.useContext(FootnoteContext);

  const createFootnoteLink = (key: string) => {
    const entry = getFootnote(key);
    return entry ? (
      <Link id={createReverseAnchor(entry.num)} href={`#${createAnchor(entry.num)}`} css={styles.sourceLink}>
        [{entry.num}]
      </Link>
    ) : null;
  };

  // collect source links
  React.useEffect(() => {
    for (const pp of proofPoints) {
      if (pp.footnote && pp.slug) {
        registerFootnote(pp.slug, pp.footnote);
      }
    }
  }, [proofPoints, registerFootnote]);

  return (
    <div css={styles.layout}>
      <div css={styles.wrapper}>
        <div css={styles.proofPoints(theme, backgroundColor?.value, fontColor?.value)}>
          {(heading || subheading) && (
            <div css={styles.headingContainer}>
              {heading && (
                <Typography
                  variant="d2semibold"
                  css={css`
                    margin-bottom: 24px;
                  `}
                >
                  {heading}
                </Typography>
              )}
              {subheading && <Typography variant="h2">{subheading}</Typography>}
            </div>
          )}
          <div css={styles.proofPointsCollection}>
            {proofPoints.map((pp) => (
              <div key={pp.slug} css={styles.proofPoint}>
                <div css={styles.keyMetricAndUnit}>
                  {pp.keyMetric && (
                    <Typography2 variant="bodyPrimary" component="div" css={styles.keyMetric} color="inherit">
                      {pp.keyMetric}
                    </Typography2>
                  )}
                  {pp.unit && (
                    <Typography2 variant="bodyPrimary" component="div" css={styles.unit} color="inherit">
                      {pp.unit}
                    </Typography2>
                  )}
                </div>
                <Typography2 variant="bodyPrimary" component="div" css={styles.description} color="inherit">
                  {pp.description}
                </Typography2>
                {pp.footnote && pp.slug && <div css={styles.sourceLinkContainer}>{createFootnoteLink(pp.slug)}</div>}
              </div>
            ))}
          </div>
          {cta?.ctaText && cta?.ctaTarget && (
            <TrackedButton
              component="a"
              href={cta?.ctaTarget}
              target={cta?.shouldOpenInNewWindow ? '_blank' : undefined}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error - string coming from Contentful
              variant={ctaButtonVariant ?? 'ghost'}
              icon={<ArrowNextIcon />}
              iconPosition="after"
              css={styles.cta}
            >
              {cta?.ctaText}
            </TrackedButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProofPoints;
